import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { appearHeight, appearWidth, configSectionsInOut } from 'animations-lib';
import { IFieldMeta } from '../../../../../interfaces/imetadata';
import { Condition, ConditionValue, NoFieldCondition } from '../../../../../utils/condition';
import { FieldType } from '../../../../../utils/fieldtype';
import { FieldConfigChangedControllerService } from '../../../data-fields/field-config-changed-controller.service';

// used by the UI only - do not export
class Operator {
  label: string;
  op: string;

  constructor(label: string, op: string) {
    this.label = label;
    this.op = op;
  }
}

export interface IChip {
  label: string;
} // TODO: This could easily just change to a string array

interface ConditionViewModel {
  condition: Condition;
  fieldLabel: string;
  operator: Operator;
  valueSummary: string;
}

// This component emits an event when any option changes (e.g. set the operator in the UI). The "parent" then
// calls back to the fieldOptions setter. Seems a bit dumb.
@Component({
    selector: 'app-field-conditions',
    templateUrl: './conditions.component.html',
    styleUrls: ['./conditions.component.scss', '../field-options.component.scss'],
    animations: [configSectionsInOut, appearHeight, appearWidth],
    standalone: false
})
export class ConditionsComponent implements OnInit {
  public FieldType: typeof FieldType = FieldType;

  private static readonly ValidFieldTypes: readonly string[] = ['string', 'integer', 'date', 'double', 'boolean'];

  private _srcFields: IFieldMeta[] | undefined = [];

  @Input() set srcFields(fields: IFieldMeta[] | undefined) {
    this._srcFields = fields;
    this.populateFieldsFromSrcFields();
  }

  get srcFields(): IFieldMeta[] | undefined {
    return this._srcFields;
  }

  @Input() set fieldOptions(conditions: Condition[] | NoFieldCondition) {
    this.initFieldOptions(conditions);
    this.hasChanged = false;
  }
  @Output() fieldOptionsChange = new EventEmitter<Condition[] | NoFieldCondition>();

  @ViewChild('chipInput') chipInput: ElementRef | undefined;

  fields: IFieldMeta[] = [];
  operators: Operator[] = [];

  expanded = false;
  hasChanged = false;
  selField: IFieldMeta | undefined;
  operator: Operator | undefined;
  operand: ConditionValue | undefined;

  chips: IChip[] = [];
  isIncludeOperator = false;

  conditions: ConditionViewModel[] = [];
  editing: ConditionViewModel | undefined;
  addingChip = false;

  private static readonly Equals = new Operator('equals', '=');
  private static readonly DoesNotEqual = new Operator('does not equal', '!=');
  private static readonly IncludesAnyOf = new Operator('includes any of', '~');
  private static readonly NotIncludesAnyOf = new Operator('does not include any of', '!~');
  private static readonly LessThan = new Operator('less than', '<');
  private static readonly GreaterThan = new Operator('greater than', '>');
  private static readonly LessThanOrEquals = new Operator('less than or equals', '<=');
  private static readonly GreaterThanOrEquals = new Operator('greater than or equals', '>=');
  private static readonly Before = new Operator('before', '<');
  private static readonly After = new Operator('after', '>');
  private static readonly NumberOperators = [
    ConditionsComponent.Equals,
    ConditionsComponent.DoesNotEqual,
    ConditionsComponent.LessThan,
    ConditionsComponent.GreaterThan,
    ConditionsComponent.LessThanOrEquals,
    ConditionsComponent.GreaterThanOrEquals,
    ConditionsComponent.IncludesAnyOf,
    ConditionsComponent.NotIncludesAnyOf,
  ];
  private static readonly DateOperators = [
    ConditionsComponent.Equals,
    ConditionsComponent.DoesNotEqual,
    ConditionsComponent.Before,
    ConditionsComponent.After,
  ];
  private static readonly StringOperators = [
    ConditionsComponent.Equals,
    ConditionsComponent.DoesNotEqual,
    ConditionsComponent.IncludesAnyOf,
    ConditionsComponent.NotIncludesAnyOf,
  ];
  private static readonly BooleanOperators = [
    ConditionsComponent.Equals,
    ConditionsComponent.DoesNotEqual
  ];

  constructor(private configChangedControllerService: FieldConfigChangedControllerService) {}

  ngOnInit(): void {
    this.hasChanged = this.configChangedControllerService.get('ConditionsComponent');

    if (this.fields.length === 0) {
      console.warn('No valid fields found for condition.');
    }

    this.updateIncludesOperator();
  }

  isValid(): boolean {
    return (this.selField !== undefined && !this.isIncludeOperator) || (this.isIncludeOperator && this.chips.length > 0);
  }

  operatorChange() {
    this.initialiseOperand(this.selField!.type);
    this.updateIncludesOperator();
  }

  onFieldSelected(_event: MatSelectChange): void {
    this.setOperators(this.selField!.type);
    this.initialiseOperand(this.selField!.type);
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
  }

  conditionTooltip(condition: ConditionViewModel): string {
    return `Edit${condition === this.editing ? 'ing' : ''} ${condition.fieldLabel} ${condition.operator.label} ${condition.valueSummary}`;
  }

  onAddChip(chip: string): void {
    if (!this.chips?.find((ic) => ic.label === chip)) {
      this.chips?.push({ label: chip });
      this.addingChip = true;
      setTimeout(() => {
        this.addingChip = false;
      }, 500);
    }
    this.clearInput(this.chipInput?.nativeElement, true);
  }

  onRemoveChip(chip: IChip | undefined): void {
    this.chips = this.chips?.filter((ic) => ic !== chip);
  }

  clearInput(inputElement: HTMLElement, focusInput: boolean): void {
    const input = inputElement as HTMLInputElement;
    input.value = '';
    if (focusInput) {
      inputElement.focus();
    }
  }

  onEditCondition(selectedCondition: ConditionViewModel): void {
    this.editing = selectedCondition === this.editing ? undefined : selectedCondition;
    this.initialiseControls(this.editing?.condition);
  }

  onRemoveCondition(selectedCondition: ConditionViewModel): void {
    const index = this.conditions.findIndex((vm) => vm === selectedCondition);
    this.conditions.splice(index, 1);
    this.updateParent();
  }

  onAddOrUpdateCondition(): void {
    const operand = this.isIncludeOperator ? this.chips : this.operand;
    const condition = new Condition(this.selField!.path, this.selField!.type, this.operator!.op, operand!);
    const conditionViewModel = this.mapConditionToViewModel(condition);
    if (!this.editing) {
      this.conditions.push(conditionViewModel);
    } else {
      const index = this.conditions.findIndex((vm) => vm === this.editing);
      this.conditions[index] = conditionViewModel;
    }

    this.updateParent();
  }

  private populateFieldsFromSrcFields(): void {
    this.fields = this._srcFields?.filter((f) => ConditionsComponent.ValidFieldTypes.includes(f.type)) || [];
  }

  private updateParent(): void {
    const conditions = this.conditions.map((vm) => vm.condition);
    this.fieldOptionsChange.emit(conditions);
    this.configChangedControllerService.update('ConditionsComponent', true);
    setTimeout(() => (this.hasChanged = true), 0);
  }

  private updateIncludesOperator(): void {
    this.isIncludeOperator = this.isIncludesOperator(this.operator?.op);
  }

  private setOperators(fieldType: string): void {
    this.operators = this.lookupOperators(fieldType);
    this.operator = this.operators[0];
  }

  private lookupOperators(fieldType: string): Operator[] {
    switch (fieldType) {
      case 'number':
      case 'double':
      case 'integer':
        return ConditionsComponent.NumberOperators;
      case 'date':
        return ConditionsComponent.DateOperators;
      case 'boolean':
        return ConditionsComponent.BooleanOperators;
      default:
        return ConditionsComponent.StringOperators;
    }
  }

  private initialiseOperand(fieldType: string): void {
    switch (fieldType) {
      case 'number':
      case 'double':
      case 'integer':
        this.operand = 0;
        break;
      case 'date':
        this.operand = new Date().toISOString().substr(0, 10);
        break;
      case 'boolean':
        this.operand = true;
        break;
      default:
        this.operand = '';
    }
  }

  private initialiseControls(condition: Condition | undefined): void {
    this.selField = condition ? this.srcFields?.find((field) => field.path === condition.fieldPath) : undefined;
    this.operators = condition ? this.lookupOperators(condition.fieldType) : [];
    const operator = this.operators.find((value) => value.op === condition?.operator);
    this.operator = operator ? operator : this.operators[0];
    this.operand = condition?.value;
    this.updateIncludesOperator();
    this.chips = this.isIncludeOperator ? (condition?.value as IChip[]) : [];
  }

  private initFieldOptions(conditions: Condition[] | NoFieldCondition) {
    this.conditions = conditions ? conditions.map((condition) => this.mapConditionToViewModel(condition)) : [];
    this.editing = undefined;
    this.initialiseControls(undefined);
  }

  private mapConditionToViewModel(condition: Condition): ConditionViewModel {
    const fieldLabel = this.srcFields?.find((field) => field.path === condition.fieldPath)!.label!;
    const operator = this.lookupOperators(condition.fieldType).find((value) => value.op === condition.operator)!;
    const valueSummary = this.isIncludesOperator(condition.operator)
      ? this.summariseChipsValue(condition.value as IChip[]).toUpperCase()
      : condition.value.toString();
    return {
      condition: condition,
      fieldLabel: fieldLabel,
      operator: operator,
      valueSummary: valueSummary,
    };
  }

  private summariseChipsValue(chipsValue: IChip[]): string {
    const chips = chipsValue.map((obj) => obj.label);
    const sanitisedChips = chips.map((label) => (label.trim() === '' ? "''" : label));
    const commaSeparatedChips = sanitisedChips.slice(0, -2).join(', ');
    const lastTwoChips = sanitisedChips.slice(-2).join(' and ');
    return [commaSeparatedChips, lastTwoChips].filter(Boolean).join(', ');
  }

  private isIncludesOperator(op: string | undefined): boolean {
    return op != undefined && (op === ConditionsComponent.IncludesAnyOf.op || op === ConditionsComponent.NotIncludesAnyOf.op);
  }
}
